<script setup lang="ts">
import { onMounted, ref, h, nextTick, inject, useTemplateRef } from "vue";
import {
  createCommonEmailValidator,
  formatTime,
  CSSAnimationNames
} from "@zyzgroup/core-common";
import {
  TWEEN,
  type EasingFunction,
  Color,
  getCSSVar,
  setTheme
} from "@zyzgroup/core-web";
import {
  getCEComponent,
  popTel,
  popCalendar,
  popBottom,
  popTop,
  popLeft,
  popRight,
  popCenter,
  popAbsolute,
  toast,
  loading,
  dialog,
  numberKeyboard,
  popRateXg
} from "../globalTeleport";

const commonEmailValidator = createCommonEmailValidator();

import Mouse from "../components/base/Mouse.vue";
import Page from "../components/base/Page.vue";

import PageVerticalScrollProgressBar from "../components/progress/PageVerticalScrollProgressBar.vue";
import FPSStats from "../components/fps/FPSStats.vue";
import Button from "../components/form/Button.vue";
import Accordion from "../components/form/Accordion.vue";
import Input from "../components/form/Input.vue";
import InputRange from "../components/form/InputRange.vue";
import InputRangeBox from "../components/form/InputRangeBox.vue";
import InputFileDrop from "../components/form/InputFileDrop.vue";

import BgGrid from "../components/bg/grid/BgGrid.vue";

import LayoutCenter from "../components/layout/LayoutCenter.vue";
import CardFlip from "../components/3d/CardFlip.vue";
import Cube from "../components/3d/Cube.vue";

import Popover from "../components/pop/Popover.vue";
import ColorPicker from "../components/form/ColorPicker.vue";

import PlateDisplay from "../components/plate/PlateDisplay.vue";
import PlateMobile from "../components/plate/PlateMobile.vue";

import { mapState, Context } from "../context/context";
const context = inject<Context>("context");
const changeToThemeDark = () => {
  setTheme("dark");
};
const changeToThemeLight = () => {
  setTheme("light");
};

import Image from "../components/image/Image.vue";
import ImageView from "../components/image/ImageView.vue";

const imagesrc1 = `${import.meta.env.YZ_IMAGE_PATH}/picture.jpg`;
const imagesrc2 = `${import.meta.env.YZ_IMAGE_PATH}/logo.png`;
const imagesrc3 = `${import.meta.env.YZ_IMAGE_PATH}/ad.jpg`;
const imagemarilyn = `${import.meta.env.YZ_IMAGE_PATH}/marilyn.png`;
const imagebike = `${import.meta.env.YZ_IMAGE_PATH}/bike.png`;

const imageViewMultiRef = ref();
function openImageViewMulti() {
  imageViewMultiRef.value?.open();
}

import HandWritePanel from "../components/hand/HandWritePanel.vue";
const handRef = ref();
const downloadHandWrite = () => {
  handRef.value.download();
};

import EaseGroupGraphDisplay from "../components/animate/EaseGroupGraphDisplay.vue";
const easeName = ref<string>("");
const easeFunction = ref<EasingFunction>(TWEEN.Easing.Linear.None);
function selectedEaseFunction(
  selectedEaseName: string,
  selectedEaseFunction: EasingFunction
) {
  easeName.value = selectedEaseName;
  easeFunction.value = selectedEaseFunction;
}

import InterpolationGroupGraphDisplay from "../components/animate/InterpolationGroupGraphDisplay.vue";

import CSSTransition from "../components/base/CSSTransition.vue";
const superRainbowRef = ref();
const superRainbowRef2 = ref();
const superRainbowEnter = () => {
  superRainbowRef.value?.trigger();
};
const superRainbowLeave = () => {
  superRainbowRef.value?.clear();
};
const superRainbowEnter2 = () => {
  superRainbowRef2.value?.trigger();
};
const superRainbowLeave2 = () => {
  superRainbowRef2.value?.triggerReverse();
};
import CSSAnimation from "../components/base/CSSAnimation.vue";
const CSSAnimationRefs = [];
const animationEnter = (index) => {
  CSSAnimationRefs[index]?.trigger();
};
const animationLeave = (index) => {
  CSSAnimationRefs[index]?.triggerReverse();
};
import BorderFocusText from "../components/css/BorderFocusText.vue";
const borderFocusTextRef = ref();
import Circle from "../components/css/Circle.vue";
const circleRef = ref();
const cssMouseEnter = (r) => {
  switch (r) {
    case "borderFocusTextRef":
      borderFocusTextRef.value?.start();
      break;
    case "circleRef":
      circleRef.value?.start();
      break;
  }
};
const cssMouseLeave = (r) => {
  switch (r) {
    case "borderFocusTextRef":
      borderFocusTextRef.value?.end();
      break;
    case "circleRef":
      circleRef.value?.end();
      break;
  }
};

const pageEffect = ref(CSSAnimationNames[0]);
const changePageEffect = (effect) => {
  pageEffect.value = effect;
};
const pageRef = ref();
function openPage() {
  pageRef.value?.open(pageEffect.value);
}
function closePage() {
  pageRef.value?.close(pageEffect.value);
}

import Routes, { SupportedRouteEffect } from "../components/base/Routes.vue";
const routeEffect = ref<SupportedRouteEffect>("moveToLeft");
const changeRouteEffect = (effect: SupportedRouteEffect) => {
  routeEffect.value = effect;
};
const routesRef = ref();
const routeChangeStart = (
  fromPageIndex: number,
  toPageIndex: number,
  totalPages: number
) => {
  // console.log(
  //   `routeChangeStart: ${fromPageIndex} to ${toPageIndex}, total ${totalPages}`
  // );
};
const routeChangeEnd = (
  fromPageIndex: number,
  toPageIndex: number,
  totalPages: number
) => {
  // console.log(
  //   `routeChangeEnd: ${fromPageIndex} to ${toPageIndex}, total ${totalPages}`
  // );
};
function resetPage() {
  routesRef.value?.reset([
    {
      // component: PlateMobile,
      // attributes: {}
      component: LayoutCenter,
      child: "Page 0",
      attributes: {
        style: {
          backgroundColor: Color.next(0)
        }
      }
    },
    {
      // component: PlateMobile,
      // attributes: {}
      component: LayoutCenter,
      child: "Page 1",
      attributes: {
        style: {
          backgroundColor: Color.next()
        }
      }
    },
    {
      // component: PlateMobile,
      // attributes: {}
      component: LayoutCenter,
      child: "Page 2",
      attributes: {
        style: {
          backgroundColor: Color.next()
        }
      }
    }
  ]);
}
function pushPage() {
  routesRef.value?.push({
    // component: PlateMobile,
    component: LayoutCenter,
    child: `Page ${routesRef.value.totalPages()}`,
    attributes: {
      style: {
        backgroundColor: Color.next()
      }
    }
  });
}
function popPage() {
  routesRef.value?.pop();
}
function forwardPage() {
  routesRef.value?.forward();
}
function backPage() {
  routesRef.value?.back();
}
function gotoPage() {
  routesRef.value?.goto(0);
}

import Tabs from "../components/form/Tabs.vue";
const labels = ["label1", "label2", "label3", "label4"];
const pages = [
  { component: PlateMobile, attributes: { onComplete: console.log } },
  { component: PlateMobile, attributes: { onComplete: console.log } },
  { component: PlateMobile, attributes: { onComplete: console.log } },
  { component: PlateMobile, attributes: { onComplete: console.log } }
];

import Video from "../components/video/Video.vue";

// 会被编译进源文件
// import videosrc from "@root/server-client/assets/videos/sintel.webm";

const videosrc = `${import.meta.env.YZ_PUBLIC_PATH}/videos/sintel.webm`;

const videoRef = ref();
const playVideo = () => {
  videoRef.value?.play();
};
const pauseVideo = () => {
  videoRef.value?.pause();
};

import MapTianditu from "../components/map/MapTianditu.vue";

const popBottomSample = () => {
  return popBottom(
    h(
      "div",
      {
        style: "width: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popBottomFixedSample = () => {
  return popBottom(
    h(
      "div",
      {
        style: "width: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    ),
    {
      fixed: 400, // 固定高度
      contentBgColor: "pink"
    }
  );
};
const popTopSample = () => {
  return popTop(
    h(
      "div",
      {
        style: "width: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popLeftSample = () => {
  return popLeft(
    h(
      "div",
      {
        style: "height: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popRightSample = () => {
  return popRight(
    h(
      "div",
      {
        style: "height: 100%;padding: 30px;background-color:#ffffff; "
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popCenterSample = () => {
  return popCenter(
    h(
      "div",
      {
        style:
          "width: 200px;height: 200px;padding: 30px;background-color:#ffffff;"
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    )
  );
};
const popAbsoluteSample = () => {
  return popAbsolute(
    h(
      "div",
      {
        style:
          "width: 200px;height: 200px;padding: 30px;background-color:#ffffff;"
      },
      h(getCEComponent("yz-input"), {
        placeholder: "请输入邮箱",
        validator: commonEmailValidator
      })
    ),
    {
      left: "50%",
      top: "50%"
    }
  );
};
const openDialogSample = () => {
  dialog({
    title: "标题",
    content:
      "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    showClose: true,
    cancelText: "取消",
    confirmText: "确认",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogSample2 = () => {
  dialog({
    title: "标题",
    content:
      "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    showClose: true,
    confirmText: "确认",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const dialogToastSample = () => {
  toast({ content: "中文十个字中文十个字" });
};
const dialogToastSample2 = () => {
  toast({ content: "中文十个字中文十个字", status: "success" });
};
const dialogToastSample3 = () => {
  toast({ content: "中文十个字中文十个字", status: "error" });
};
const dialogToastSample4 = () => {
  toast({ content: "中文十个字中文十个字", status: "info" });
};
const openDialogLoadingSample = () => {
  const close = loading({ content: "加载中..." });
  setTimeout(() => {
    close();
  }, 2000);
};
const openDialogMiniSample = () => {
  dialog({
    title: "标题",
    content: "ttttttttt",
    cancelText: "取消",
    confirmText: "确认",
    mode: "mini",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogMiniSample2 = () => {
  dialog({
    title: "标题",
    content: "ttttttttt",
    confirmText: "确认",
    mode: "mini",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogRedSample = () => {
  dialog({
    title: "标题",
    content: "redredredredredredredredredredredredredredred",
    cancelText: "",
    confirmText: "确认",
    colorPattern: "xg-red",
    mode: "icon-btns",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogRedSample2 = () => {
  dialog({
    title: "",
    content: "redredredredredredredredredredredredredredred",
    cancelText: "取消",
    confirmText: "确认",
    colorPattern: "xg-red",
    mode: "btns",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogBlueSample = () => {
  dialog({
    title: "标题",
    content: "blueblueblueblueblueblueblueblueblueblueblueblueblueblueblue",
    cancelText: "取消",
    confirmText: "确认",
    colorPattern: "xg-blue",
    mode: "icon-btns",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openDialogBlueSample2 = () => {
  dialog({
    title: "标题",
    content: "blueblueblueblueblueblueblueblueblueblueblueblueblueblueblue",
    cancelText: "取消",
    confirmText: "确认",
    colorPattern: "xg-blue",
    mode: "btns",
    onCancel: () => {
      console.log("cancel");
    },
    onConfirm: () => {
      console.log("confirm");
    }
  });
};
const openNumberKeyboardSample = () => {
  numberKeyboard({
    min: 100.02,
    max: 200.345,
    onNumber: (n) => {
      console.log("numberKeyboard: " + n);
    },
    onError: (msg) => {
      toast({ content: msg });
    }
  });
};
const openPopCalendarSample = () => {
  popCalendar({
    title: "选择开始日期",
    initDay: "2026-03-05",
    startDay: "2026-03-06",
    excludeDays: ["2026-03-20", "2026-03-21"],
    selectedDays: [
      "2026-02-28",
      "2026-03-03",
      "2026-03-06",
      "2026-03-23",
      "2026-04-14"
    ],
    onConfirm: (d) => {
      console.log(d);
    }
  });
};

import Loading1 from "../components/loading/Loading1.vue";
import Loading2 from "../components/loading/Loading2.vue";
import Loading3 from "../components/loading/Loading3.vue";
import Loading4 from "../components/loading/Loading4.vue";
import Loading5 from "../components/loading/Loading5.vue";
import Loading6 from "../components/loading/Loading6.vue";
import Loading7 from "../components/loading/Loading7.vue";
import IconLoading from "../components/icon/IconLoading.vue";
import Loading9 from "../components/loading/Loading9.vue";
import Loading10 from "../components/loading/Loading10.vue";
import Loading11 from "../components/loading/Loading11.vue";

import RingText from "../components/chat/RingText.vue";

import ClockFlipper from "../components/clock/ClockFlipper.vue";
const clockFlipper = ref();
const startClockFlipper = () => {
  clockFlipper.value?.start(formatTime(new Date()));
};
const pauseClockFlipper = () => {
  clockFlipper.value?.pause();
};

import RingIndicator from "../components/chat/RingIndicator.vue";
const percent = ref(0);
const resetRingIndicator = () => {
  percent.value = 0;
};
const addRingIndicator = () => {
  percent.value += Math.random() * 30;
};

let timestamp = ref("1695000491000");
setTimeout(() => {
  timestamp.value = new Date().getTime().toFixed(0);
  // console.log(timestamp.value)
}, 3000);

import SVGPrompt from "../components/svg/SVGPrompt.vue";
import SVGQuestion from "../components/svg/SVGQuestion.vue";
import SVGFail from "../components/svg/SVGFail.vue";
import SVGSuccess from "../components/svg/SVGSuccess.vue";
import SVGWaiting from "../components/svg/SVGWaiting.vue";
import SVGLock from "../components/svg/SVGLock.vue";
import SVGDown from "../components/svg/SVGDown.vue";
import SVGAdd from "../components/svg/SVGAdd.vue";
import SVGDelete from "../components/svg/SVGDelete.vue";
const blue = getCSSVar("--yz-xg-blue", "#5a82f0");
import Icon from "../components/form/Icon.vue";
const icon = ref("carbon:accessibility");
const color = ref(blue);
const count = ref(0);
const iconClick = () => {
  icon.value =
    count.value++ % 2 == 0 ? "icon-park:add-subtract" : "carbon:accessibility";
};

import IconArrowRight from "../components/icon/IconArrowRight.vue";

import RadioGroup from "../components/form/RadioGroup.vue";
import CheckboxGroup from "../components/form/CheckboxGroup.vue";
import Switch from "../components/form/Switch.vue";
import Select from "../components/form/Select.vue";
import SelectAbsolute from "../components/form/SelectAbsolute.vue";
import SlideLeftItem from "../components/form/SlideLeftItem.vue";
import Status from "../components/form/Status.vue";

import IconStatus from "../components/icon/IconStatus.vue";
import Empty from "../components/panel/Empty.vue";

import Calendar from "../components/form/Calendar.vue";
import RateXg from "../components/rate-xg/RateXg.vue";
import RateXgPanel from "../components/rate-xg/RateXgPanel.vue";
import { StoredRate } from "../components/rate-xg/common";
function openRated() {
  popRateXg({
    editable: false,
    init: {
      rate: 5,
      options: ["计费清晰准确", "服务管理到位", "整体体验不错"],
      // options: ["计费清晰准确", "服务管理到位"],
      // options: ["计费清晰准确"],
      detail: "😈😈😈"
    }
  });
}

onMounted(() => {});
</script>

<template>
  <div>
    <div
      style="
        position: fixed;
        top: 0;
        left: 0;
        z-index: 100;
        width: 100%;
        height: 6px;
      "
    >
      <PageVerticalScrollProgressBar></PageVerticalScrollProgressBar>
    </div>

    <FPSStats></FPSStats>

    <Button @throttledClick="changeToThemeDark">theme:dark</Button>
    <Button @throttledClick="changeToThemeLight">theme:light</Button>

    <div style="margin-top: 200px">
      <RateXgPanel :editable="true"></RateXgPanel>
    </div>
    <div style="margin: 20px 0">
      <RateXgPanel
        :editable="false"
        :init="{ rate: 4, options: ['text'], detail: 'yyyyy' }"
      ></RateXgPanel>
    </div>
    <div style="margin: 20px 15px">
      <Button @throttledClick="openRated">打开已评价</Button>
    </div>
    <div style="margin: 20px 15px">
      <RateXg :editable="true"></RateXg>
    </div>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Button</template>
      <template #content>
        <div>
          <Button colorPattern="xg-blue">colorPattern:blue</Button>
          <Button
            colorPattern="xg-blue"
            :disabled="true"
          >
            disabled
          </Button>
          <Button
            colorPattern="xg-blue"
            mode="normal"
          >
            normal
          </Button>
          <Button
            colorPattern="xg-blue"
            mode="normal"
            :disabled="true"
          >
            disabled
          </Button>
          <Button
            colorPattern="xg-blue"
            mode="gradient"
          >
            gradient
          </Button>
          <Button
            colorPattern="xg-blue"
            mode="gradient"
            :disabled="true"
          >
            gradient-disabled
          </Button>
          <Button
            colorPattern="xg-blue"
            mode="secondary"
          >
            secondary
          </Button>
          <br />
          <Button colorPattern="xg-red">colorPattern:red</Button>
          <Button
            colorPattern="xg-red"
            :disabled="true"
          >
            disabled
          </Button>
          <Button
            colorPattern="xg-red"
            mode="normal"
          >
            normal
          </Button>
          <Button
            colorPattern="xg-red"
            mode="normal"
            :disabled="true"
          >
            disabled
          </Button>
          <Button
            colorPattern="xg-red"
            mode="gradient"
          >
            gradient
          </Button>
          <Button
            colorPattern="xg-red"
            mode="gradient"
            :disabled="true"
          >
            gradient-disabled
          </Button>
          <br />
          <Button colorPattern="xg-green">colorPattern:green</Button>
          <Button
            colorPattern="xg-green"
            mode="outline"
          >
            outline
          </Button>
          <Button
            colorPattern="xg-green"
            mode="outline"
            :disabled="true"
          >
            disabled
          </Button>
          <br />
          <Button colorPattern="ks-green">colorPattern:ks-green</Button>
          <Button
            colorPattern="ks-green"
            :disabled="true"
          >
            disabled
          </Button>
          <Button
            colorPattern="ks-green"
            mode="secondary"
          >
            secondary
          </Button>
          <br />
          <Button colorPattern="yz-green">colorPattern:yz-green</Button>
          <Button
            colorPattern="yz-green"
            :disabled="true"
          >
            disabled
          </Button>
          <Button
            colorPattern="yz-green"
            mode="secondary"
          >
            secondary
          </Button>
          <br />
          <Button mode="transparent">transparent</Button>
          <Button mode="normal-transparent">normal-transparent</Button>
          <Button
            mode="transparent"
            :disabled="true"
          >
            disabled
          </Button>
          <Button mode="empty">empty</Button>
          <br />
          <Button mode="outline">outline</Button>
          <Button
            mode="outline"
            :disabled="true"
          >
            disabled
          </Button>
          <Button
            mode="outline"
            :capsule="true"
          >
            outline
          </Button>
          <Button
            mode="outline"
            :disabled="true"
            :capsule="true"
          >
            disabled
          </Button>
          <br />
          <Button width="100%">100%</Button>
          <br />
          <Button
            :icon="icon"
            @throttledClick="iconClick"
          >
            icon
          </Button>
          <Button
            size="secondary"
            :icon="icon"
            @throttledClick="iconClick"
          >
            small
          </Button>
          <br />
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Dialog</template>
      <template #content>
        <div>
          <Button @click="openDialogSample">dialog</Button>
          <Button @click="openDialogSample2">dialog-2</Button>
          <Button @click="openDialogMiniSample">dialog-mini</Button>
          <Button @click="openDialogMiniSample2">dialog-mini-2</Button>
          <Button @click="openDialogLoadingSample">dialog-loading</Button>
          <Button @click="dialogToastSample">dialog-toast</Button>
          <Button @click="dialogToastSample2">dialog-toast-success</Button>
          <Button @click="dialogToastSample3">dialog-toast-error</Button>
          <Button @click="dialogToastSample4">dialog-toast-info</Button>
          <Button @click="openDialogRedSample">dialog-icon-btns-red</Button>
          <Button @click="openDialogBlueSample">dialog-icon-btns-blue</Button>
          <Button @click="openDialogRedSample2">dialog-btns-red</Button>
          <Button @click="openDialogBlueSample2">dialog-btns-blue</Button>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - RadioGroup</template>
      <template #content>
        <div>
          <RadioGroup
            mode="primary-round"
            :options="['111', '222', '333', '444']"
            :initIndex="2"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></RadioGroup>
        </div>
        <div>
          <RadioGroup
            mode="primary-rect"
            :options="['111', '222', '333', '444']"
            :initIndex="2"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></RadioGroup>
        </div>
        <div>
          <RadioGroup
            mode="transparent-round"
            :options="['111', '222', '333', '444']"
            :initIndex="2"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></RadioGroup>
        </div>
        <div>
          <RadioGroup
            mode="transparent-rect"
            :options="['111', '222', '333', '444']"
            :initIndex="2"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></RadioGroup>
        </div>
        <div>
          <RadioGroup
            mode="double-round"
            :options="['111', '222', '333', '444']"
            :initIndex="2"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></RadioGroup>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - CheckboxGroup</template>
      <template #content>
        <div>
          <CheckboxGroup
            mode="primary-round"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
        <div>
          <CheckboxGroup
            mode="primary-rect"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
        <div>
          <CheckboxGroup
            mode="transparent-round"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
        <div>
          <CheckboxGroup
            mode="transparent-rect"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
        <div>
          <CheckboxGroup
            mode="double-round"
            :options="['111', '222', '333', '444']"
            :initIndexs="[1, 2]"
            :disabledIndexs="[2, 3]"
            @change="console.log"
          ></CheckboxGroup>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Switch</template>
      <template #content>
        <div>
          <Switch
            :init="true"
            color-bg-on="#ffffff"
            color-bg-off="#ffffff"
            @change="console.log"
          ></Switch>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Select</template>
      <template #content>
        <div style="height: 300px">
          <Select
            :options="['options1', 'options2', 'options33333333333']"
            :initIndex="2"
            @change="console.log"
          ></Select>
          <div style="width: 200px">
            <SelectAbsolute
              :options="[
                'options1',
                'options2',
                'options33333333333',
                'options4',
                'options5555',
                'options666666666666666666666666666666666666666666666666',
                'options7',
                'options8'
              ]"
              :initIndex="1"
              @change="console.log"
            ></SelectAbsolute>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - SlideLeftItem</template>
      <template #content>
        <div style="margin: 30px">
          <SlideLeftItem @delete="() => console.log('delete')">
            <div style="background-color: #ffffff">
              <Button>测试slide</Button>
            </div>
          </SlideLeftItem>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Input</template>
      <template #content>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            :validator="commonEmailValidator"
          ></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            placeholderType="underline"
            :showError="true"
            :validator="commonEmailValidator"
          ></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            :validator="commonEmailValidator"
            defaultValue="489573239@qq.com"
            @input="console.log"
          ></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            placeholderType="jump"
            :validator="commonEmailValidator"
          ></Input>
        </div>
        <div style="margin-top: 20px">
          <Input
            placeholder="请输入邮箱"
            placeholderType="jump-each"
            :validator="commonEmailValidator"
          ></Input>
        </div>
        <div style="width: 200px; height: 200px">
          <InputFileDrop @drop="console.log"></InputFileDrop>
        </div>
        <div style="width: 200px; margin-top: 20px; margin-bottom: 20px">
          <InputRange
            :defaultValue="20"
            :min="0"
            :max="200"
            :step="2"
          ></InputRange>
          <InputRangeBox
            :defaultValue="20"
            :min="0"
            :max="200"
            :step="2"
          ></InputRangeBox>
        </div>

        <div v-timestamp="`yyyy-mm-dd hh:ii:ss`">1695000491000</div>
        <div v-timestamp>{{ timestamp }}</div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - Calendar</template>
      <template #content>
        <div style="padding: 30px 0; font-size: 16px">
          <Button @click="openPopCalendarSample">popCalendar</Button>
          <div style="width: 300px">
            <div style="width: 300px">
              <!--  :endDay="'2026-03-07'" -->
              <Calendar
                :title="'选择开始日期'"
                :initDay="'2026-03-05'"
                :startDay="'2026-03-06'"
                :excludeDays="['2026-03-20', '2026-03-21']"
                :selectedDays="[
                  '2026-02-28',
                  '2026-03-03',
                  '2026-03-06',
                  '2026-03-23',
                  '2026-04-14'
                ]"
                @confirm="console.log"
              ></Calendar>
            </div>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Form Widgets - bg</template>
      <template #content>
        <div style="padding: 30px 0; font-size: 16px">
          <div style="width: 200px; height: 200px">
            <BgGrid></BgGrid>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Tabs</template>
      <template #content>
        <div style="padding: 30px 0; font-size: 16px">
          <div style="width: 100%; height: 300px">
            <Tabs
              :labels="labels"
              :pages="pages"
            ></Tabs>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Icon + SVG</template>
      <template #content>
        <div style="margin-top: 30px">
          <Status status="wait"></Status>
          <Status status="processing"></Status>
          <Status status="success"></Status>
          <Status status="error"></Status>
        </div>
        <div style="margin: 30px; height: 300px">
          <icon
            :icon="icon"
            :color="color"
            @click="iconClick"
          ></icon>
          <div style="width: 24px"><IconArrowRight></IconArrowRight></div>
          <SVGPrompt></SVGPrompt>
          <SVGQuestion></SVGQuestion>
          <SVGFail></SVGFail>
          <SVGSuccess></SVGSuccess>
          <SVGWaiting></SVGWaiting>
          <SVGLock></SVGLock>
          <SVGDown></SVGDown>
          <div style="display: inline-block; width: 24px; height: 24px">
            <SVGAdd></SVGAdd>
          </div>
          <div style="display: inline-block; width: 24px; height: 24px">
            <SVGDelete></SVGDelete>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Panel - IconStatus</template>
      <template #content>
        <div style="width: 64px">
          <IconStatus status="success"></IconStatus>
        </div>
        <div style="width: 64px">
          <IconStatus status="error"></IconStatus>
        </div>
        <div style="width: 64px">
          <IconStatus status="info"></IconStatus>
        </div>
        <div style="width: 64px">
          <IconStatus status="waiting"></IconStatus>
        </div>
        <div style="width: 64px">
          <IconStatus
            status="success"
            mode="transparent"
          ></IconStatus>
        </div>
        <div style="width: 64px">
          <IconStatus
            status="error"
            mode="transparent"
          ></IconStatus>
        </div>
        <div style="width: 64px">
          <IconStatus
            status="info"
            mode="transparent"
          ></IconStatus>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Panel - Empty</template>
      <template #content>
        <div style="width: 300px; height: 300px; border: 1px solid red">
          <Page><Empty title="其他通用提示信息"></Empty></Page>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Plate-车牌</template>
      <template #content>
        <div>
          <PlateDisplay
            plate="苏U88888"
            plateColor="蓝牌"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="绿牌"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黄牌"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="黄绿"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黑牌"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="白牌"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏UNKNOW"
            plateColor="未知"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="无牌车"
            plateColor="b"
            display="leading-chinese"
          ></PlateDisplay>
          <br />
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="蓝牌"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="绿牌"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黄牌"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="黄绿"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黑牌"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="白牌"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏UNKNOW"
            plateColor="未知"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="无牌车"
            plateColor="b"
            display="leading-dot"
          ></PlateDisplay>
          <br />
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="b"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="g"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="y"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="q"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="d"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="w"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏UNKNOW"
            plateColor="未知"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="无牌车"
            plateColor="b"
            display="leading-plaintext"
          ></PlateDisplay>
          <br />
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="蓝牌"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="绿牌"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黄牌"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U123456"
            plateColor="黄绿"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黑牌"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="白牌"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏UNKNOW"
            plateColor="未知"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="无牌车"
            plateColor="b"
            display="bg-mockcar"
          ></PlateDisplay>
          <br />
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="蓝牌"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U888888"
            plateColor="绿牌"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黄牌"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U123456"
            plateColor="黄绿"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="黑牌"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏U88888"
            plateColor="白牌"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="苏UNKNOW"
            plateColor="未知"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <PlateDisplay
            plate="无牌车"
            plateColor="b"
            display="bg-simple"
          ></PlateDisplay>
          <br />
          <br />
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Pop</template>
      <template #content>
        <div>
          <div style="margin-top: 30px">
            <Button @click="popBottomSample">pop-bottom</Button>
            <Button @click="popBottomFixedSample">pop-bottom-fixed</Button>
            <Button @click="popTopSample">pop-top</Button>
            <Button @click="popLeftSample">pop-left</Button>
            <Button @click="popRightSample">pop-right</Button>
            <Button @click="popCenterSample">pop-center</Button>
            <Button @click="popAbsoluteSample">pop-absolute</Button>
            <Button @click="() => popTel('400-158-8363')">pop-tel</Button>
            <ColorPicker @color="console.log"></ColorPicker>
          </div>

          <div style="margin-top: 30px">
            <Popover>
              <template #panel>
                <div
                  style="width: 300px; height: 200px; background-color: white"
                >
                  Panel
                </div>
              </template>
            </Popover>
          </div>

          <!-- <div style="margin-top: 30px">
            <icon
              :icon="icon"
              :color="color"
              @click="click"
            ></icon>
            <SVGPrompt></SVGPrompt>
            <SVGQuestion></SVGQuestion>
            <SVGFail></SVGFail>
            <SVGSuccess></SVGSuccess>
            <SVGWaiting></SVGWaiting>
            <SVGLock></SVGLock>
            <SVGDown></SVGDown>
            <div style="display: inline-block; width: 24px; height: 24px">
              <SVGAdd></SVGAdd>
            </div>
            <div style="display: inline-block; width: 24px; height: 24px">
              <SVGDelete></SVGDelete>
            </div>
          </div> -->
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Keyboard</template>
      <template #content>
        <div>
          <Button @click="openNumberKeyboardSample">NumberKeyboard</Button>

          <PlateMobile
            stylePattern="simple"
            :supportWhitePlate="true"
            @complete="console.log"
          ></PlateMobile>
          <PlateMobile
            stylePattern="simple"
            :supportWhitePlate="false"
            @complete="console.log"
          ></PlateMobile>
          <PlateMobile
            stylePattern="dot"
            :supportWhitePlate="true"
            @complete="console.log"
          ></PlateMobile>
          <PlateMobile
            stylePattern="dot"
            :supportWhitePlate="false"
            @complete="console.log"
          ></PlateMobile>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Image + ImageView</template>
      <template #content>
        <div style="width: 100%; height: 200px">
          <Image
            :src="imagesrc1"
            mode="aspectFill"
          ></Image>
        </div>
        <div>
          <Button @click="openImageViewMulti">open image view</Button>
          <ImageView
            ref="imageViewMultiRef"
            :urls="[imagesrc1, imagesrc2, imagesrc3, imagesrc2]"
          ></ImageView>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>手写板</template>
      <template #content>
        <div>
          <HandWritePanel ref="handRef"></HandWritePanel>
          <Button @click="downloadHandWrite">下载</Button>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>缓动函数 {{ easeName }}</template>
      <template #content>
        <div>
          <EaseGroupGraphDisplay
            @selectedEaseFunction="selectedEaseFunction"
          ></EaseGroupGraphDisplay>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>插值函数</template>
      <template #content>
        <div>
          <InterpolationGroupGraphDisplay></InterpolationGroupGraphDisplay>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>CSSTransition + CSSAnimation + Effects</template>
      <template #content>
        <Mouse
          @enter="superRainbowEnter"
          @leave="superRainbowLeave"
        >
          <CSSTransition
            ref="superRainbowRef"
            name="superRainbow"
          >
            <div style="width: 100px; height: 100px">
              <Image :src="imagebike" />
            </div>
          </CSSTransition>
        </Mouse>
        <Mouse
          @enter="superRainbowEnter2"
          @leave="superRainbowLeave2"
        >
          <CSSAnimation
            ref="superRainbowRef2"
            name="superRainbow"
            style="background: red"
            :duration="3000"
          >
            <img :src="imagemarilyn" />
          </CSSAnimation>
        </Mouse>
        <Mouse
          @enter="() => cssMouseEnter('borderFocusTextRef')"
          @leave="() => cssMouseLeave('borderFocusTextRef')"
        >
          <BorderFocusText
            ref="borderFocusTextRef"
            :text="'当前时段'"
          >
            <div
              style="width: 200px; line-height: 50px; background-color: #ffffff"
            >
              something inner
            </div>
          </BorderFocusText>
        </Mouse>
        <Mouse
          @enter="() => cssMouseEnter('circleRef')"
          @leave="() => cssMouseLeave('circleRef')"
        >
          <Circle ref="circleRef">
            <div
              style="
                width: 4em;
                height: 4em;
                line-height: 4em;
                text-align: center;
                background-color: pink;
                border-radius: 100%;
              "
            >
              Circle
            </div>
          </Circle>
        </Mouse>
        <div style="padding-bottom: 300px">
          <div
            v-for="(item, index) in CSSAnimationNames"
            :key="item"
            style="padding: 10px 0; border: 1px solid #cccccc"
          >
            <Mouse
              @enter="
                (e) => {
                  animationEnter(index);
                }
              "
              @leave="
                (e) => {
                  animationLeave(index);
                }
              "
            >
              <CSSAnimation
                :ref="(el) => CSSAnimationRefs.push(el)"
                :name="item"
              >
                CSSAnimation {{ item }}
              </CSSAnimation>
            </Mouse>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Page + Routes</template>
      <template #content>
        <div class="btns">
          <RadioGroup
            :options="[...CSSAnimationNames]"
            @change="changePageEffect"
          ></RadioGroup>
        </div>
        <div>
          <Button @click="openPage">page: open</Button>
          <Button @click="closePage">page: close</Button>
        </div>
        <div
          style="
            width: 100%;
            height: 300px;
            border: 1px solid black;
            background-color: #333;
          "
        >
          <Page
            ref="pageRef"
            :name="pageEffect"
          >
            <PlateMobile></PlateMobile>
          </Page>
        </div>
        <div style="padding: 30px 0; font-size: 16px">
          <div class="btns">
            <RadioGroup
              :options="[
                'moveToLeft',
                'moveToRight',
                'moveToTop',
                'moveToBottom',
                'moveFromLeftPrevFade',
                'moveFromRightPrevFade',
                'moveFromTopPrevFade',
                'moveFromBottomPrevFade',
                'moveToLeftFade',
                'moveToRightFade',
                'moveToTopFade',
                'moveToBottomFade',
                'moveToLeftEasing',
                'moveToRightEasing',
                'moveToTopEasing',
                'moveToBottomEasing',
                'moveFromLeftPrevScaleToSmall',
                'moveFromRightPrevScaleToSmall',
                'moveFromTopPrevScaleToSmall',
                'moveFromBottomPrevScaleToSmall',
                'scaleSmall',
                'scaleLarge',
                'scaleToLargePrevScaleToSmall',
                'scaleFromSmallPrevMoveToLeft',
                'scaleFromSmallPrevMoveToRight',
                'scaleFromSmallPrevMoveToTop',
                'scaleFromSmallPrevMoveToBottom',
                'glueToLeft',
                'glueToRight',
                'glueToTop',
                'glueToBottom',
                'flipToLeft',
                'flipToRight',
                'flipToTop',
                'flipToBottom',
                'rotateFall',
                'newspaper',
                'moveFromLeftPrevPush',
                'moveFromRightPrevPush',
                'moveFromTopPrevPush',
                'moveFromBottomPrevPush',
                'pullFromLeftPrevPush',
                'pullFromRightPrevPush',
                'pullFromTopPrevPush',
                'pullFromBottomPrevPush',
                'moveFromLeftFadePrevFold',
                'moveFromRightFadePrevFold',
                'moveFromTopFadePrevFold',
                'moveFromBottomFadePrevFold',
                'moveToLeftFadePrevUnfold',
                'moveToRightFadePrevUnfold',
                'moveToTopFadePrevUnfold',
                'moveToBottomFadePrevUnfold',
                'rotateRoomLeftIn',
                'rotateRoomRightIn',
                'rotateRoomTopIn',
                'rotateRoomBottomIn',
                'rotateCubeLeftIn',
                'rotateCubeRightIn',
                'rotateCubeTopIn',
                'rotateCubeBottomIn',
                'rotateCarouselLeftIn',
                'rotateCarouselRightIn',
                'rotateCarouselTopIn',
                'rotateCarouselBottomIn',
                'rotateSidesIn',
                'rotateSlideIn'
              ]"
              @change="changeRouteEffect"
            ></RadioGroup>
          </div>
          <div>
            <Button @click="pushPage">push</Button>
            <Button @click="popPage">pop</Button>
            <Button @click="gotoPage">goto0</Button>
            <Button @click="resetPage">reset</Button>
            <Button @click="backPage">back</Button>
            <Button @click="forwardPage">forward</Button>
          </div>
          <div
            style="
              width: 100%;
              height: 200px;
              border: 1px solid black;
              background-color: #333;
            "
          >
            <Routes
              ref="routesRef"
              :effect="routeEffect"
              @routeChangeStart="routeChangeStart"
              @routeChangeEnd="routeChangeEnd"
            />
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Video</template>
      <template #content>
        <div>
          <div>
            <Button @click="playVideo">play</Button>
            <Button @click="pauseVideo">pause</Button>
          </div>
          <Video
            ref="videoRef"
            :src="videosrc"
            :autoplay="false"
          ></Video>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>天地图</template>
      <template #content>
        <div style="height: 500px">
          <MapTianditu>
            <template v-slot="map">
              <div
                id="mapTiandituDiv"
                :style="{
                  width: map.width,
                  height: map.height
                }"
              ></div>
            </template>
          </MapTianditu>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Layout</template>
      <template #content>
        <div style="padding: 30px 0; font-size: 16px">
          <div style="display: inline-block; width: 2rem; height: 4rem">
            <Page>
              <template #header>
                <div :style="{ height: '1rem', backgroundColor: '#5a82f0' }">
                  header
                </div>
              </template>
              <template #footer>
                <div :style="{ height: '1rem', backgroundColor: '#5a82f0' }">
                  footer
                </div>
              </template>
              <div>
                <div>main1</div>
                <div>main2</div>
                <div>main3</div>
                <div>main4</div>
                <div>main5</div>
                <div>main6</div>
                <div>main7</div>
                <div>main8</div>
                <div>main9</div>
                <div>main10</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main</div>
                <div>main-last</div>
              </div>
            </Page>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>3D</template>
      <template #content>
        <div style="padding: 30px 0; font-size: 16px; display: flex">
          <div style="width: 100px; height: 200px">
            <CardFlip>
              <template #front>
                <div style="background-color: #ff0000">front1</div>
              </template>
              <template #back>
                <div style="background-color: #00ff00">back1</div>
              </template>
            </CardFlip>
          </div>
          <div style="width: 100px; height: 200px">
            <CardFlip type="slide">
              <template #front>
                <div style="background-color: #0000ff">front2</div>
              </template>
              <template #back>
                <div style="background-color: #00ff00">back2</div>
              </template>
            </CardFlip>
          </div>
          <div>
            <Cube></Cube>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Loading</template>
      <template #content>
        <div
          style="padding: 30px 0; font-size: 16px"
          class="loadings"
        >
          <div>
            <Loading1></Loading1>
          </div>
          <div>
            <Loading2></Loading2>
          </div>
          <div>
            <Loading3></Loading3>
          </div>
          <div>
            <Loading4></Loading4>
          </div>
          <div>
            <Loading5></Loading5>
          </div>
          <div>
            <Loading6></Loading6>
          </div>
          <div>
            <Loading7></Loading7>
          </div>
          <div>
            <IconLoading></IconLoading>
          </div>
          <div>
            <Loading9></Loading9>
          </div>
          <div>
            <Loading10></Loading10>
          </div>
          <div>
            <Loading11></Loading11>
          </div>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>ClockFlipper</template>
      <template #content>
        <Button @click="startClockFlipper">start</Button>
        <Button @click="pauseClockFlipper">pause</Button>
        <div style="width: 460px; height: 120px">
          <ClockFlipper ref="clockFlipper"></ClockFlipper>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Chat - RingText</template>
      <template #content>
        <div style="width: 200px; height: 200px">
          <RingText
            :text="'开启中...'"
            :secondText="'请您耐心等待'"
          ></RingText>
        </div>
      </template>
    </Accordion>

    <Accordion style="margin: 30px">
      <template #title>Chat - RingIndicator</template>
      <template #content>
        <Button @click="resetRingIndicator">reset</Button>
        <Button @click="addRingIndicator">random</Button>
        <div style="width: 400px; height: 200px">
          <RingIndicator :percent="percent"></RingIndicator>
        </div>
        <div style="width: 400px; height: 200px">
          <RingIndicator
            :half="true"
            :percent="percent"
          ></RingIndicator>
        </div>
      </template>
    </Accordion>
  </div>
</template>

<style scoped>
.loadings > div {
  display: inline-block;
  width: 100px;
  height: 100px;
  overflow: hidden;
  border: 1px solid green;
}
</style>
